import { useEffect, useState } from 'react';
import { useStrings } from '@/i18n/Localization';
import {
  enqueueCreateChannel,
  enqueueCreateGroup,
  enqueueJoinConversation,
} from '@/store/localStore';
import { useSession } from '@/transport/SessionProvider';
import type { Hex } from '@/store/ids';
import dialogStyles from '@/components/DeleteDialog.module.css';
import styles from '@/components/panels.module.css';

export interface NewGroupDialogProps {
  onCreated: (conversationId: Hex) => void;
  onClose: () => void;
}

/**
 * Creating a group.
 *
 * The history policy is chosen here and nowhere else: Section 06 fixes it at
 * creation, and changing it later requires an owner action and a new policy
 * version. The note under the control says so, because a setting that looks
 * like a preference but is really a one-time decision is the kind of thing
 * people get wrong once and cannot undo.
 */
export function NewGroupDialog(props: NewGroupDialogProps) {
  const { t } = useStrings();
  const { account, worker } = useSession();
  const [title, setTitle] = useState('');
  const [historyPolicy, setHistoryPolicy] = useState('ALL_HISTORY');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        props.onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [props]);

  const create = async () => {
    if (!account || title.trim().length === 0) {
      return;
    }
    try {
      const created = await enqueueCreateGroup({
        title: title.trim(),
        initialMembers: [],
        historyPolicy,
        originDeviceId: account.deviceId,
      });
      worker?.notifyOutboxChanged();
      props.onCreated(created.conversationId);
    } catch {
      setError(t('status.localFailure'));
    }
  };

  return (
    <div className={dialogStyles.backdrop}>
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-group-title"
      >
        <h2 id="new-group-title" className={dialogStyles.title}>
          {t('group.createTitle')}
        </h2>

        <div className={styles.field}>
          <label htmlFor="new-group-name">{t('group.name')}</label>
          <input
            id="new-group-name"
            className={styles.input}
            dir="auto"
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="new-group-history">{t('group.historyPolicy')}</label>
          <select
            id="new-group-history"
            className={styles.select}
            value={historyPolicy}
            onChange={(event) => {
              setHistoryPolicy(event.target.value);
            }}
          >
            <option value="ALL_HISTORY">{t('group.historyAll')}</option>
            <option value="SINCE_JOIN">{t('group.historySinceJoin')}</option>
          </select>
          <p className={styles.hint}>{t('group.historyFixed')}</p>
        </div>

        {error === null ? null : (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <div className={dialogStyles.actions}>
          <button type="button" className={dialogStyles.cancel} onClick={props.onClose}>
            {t('group.cancel')}
          </button>
          <button
            type="button"
            className={dialogStyles.confirm}
            disabled={title.trim().length === 0}
            onClick={() => {
              void create();
            }}
          >
            {t('group.createAction')}
          </button>
        </div>
      </div>
    </div>
  );
}

export interface NewChannelDialogProps {
  onCreated: (conversationId: Hex) => void;
  onClose: () => void;
}

/**
 * Creating a channel.
 *
 * A public username is optional and is what makes the channel readable without
 * a subscription, so the hint says that plainly rather than describing it as a
 * vanity address.
 */
export function NewChannelDialog(props: NewChannelDialogProps) {
  const { t } = useStrings();
  const { account, worker } = useSession();
  const [title, setTitle] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        props.onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [props]);

  const create = async () => {
    if (!account || title.trim().length === 0) {
      return;
    }
    try {
      const created = await enqueueCreateChannel({
        title: title.trim(),
        publicUsername: username.trim().length === 0 ? null : username.trim().replace(/^@/, ''),
        originDeviceId: account.deviceId,
      });
      worker?.notifyOutboxChanged();
      props.onCreated(created.conversationId);
    } catch {
      setError(t('status.localFailure'));
    }
  };

  return (
    <div className={dialogStyles.backdrop}>
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-channel-title"
      >
        <h2 id="new-channel-title" className={dialogStyles.title}>
          {t('channel.createTitle')}
        </h2>

        <div className={styles.field}>
          <label htmlFor="new-channel-name">{t('channel.name')}</label>
          <input
            id="new-channel-name"
            className={styles.input}
            dir="auto"
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="new-channel-username">{t('channel.username')}</label>
          <input
            id="new-channel-username"
            className={styles.input}
            value={username}
            onChange={(event) => {
              setUsername(event.target.value);
            }}
          />
          <p className={styles.hint}>{t('channel.usernameHint')}</p>
        </div>

        {error === null ? null : (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <div className={dialogStyles.actions}>
          <button type="button" className={dialogStyles.cancel} onClick={props.onClose}>
            {t('group.cancel')}
          </button>
          <button
            type="button"
            className={dialogStyles.confirm}
            disabled={title.trim().length === 0}
            onClick={() => {
              void create();
            }}
          >
            {t('channel.createAction')}
          </button>
        </div>
      </div>
    </div>
  );
}

export interface JoinDialogProps {
  onClose: () => void;
}

/**
 * Joining by invitation link or public username.
 *
 * Section 06 allows a join to require approval, in which case there is no
 * conversation to open yet. The dialog says the request was sent rather than
 * navigating to a group the account has not been admitted to.
 */
export function JoinDialog(props: JoinDialogProps) {
  const { t } = useStrings();
  const { account, worker } = useSession();
  const [value, setValue] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        props.onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [props]);

  const join = async () => {
    if (!account) {
      return;
    }
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return;
    }
    // A leading "@" is the only reliable discriminator between a public
    // username and an invitation token, and it is how a username is written
    // everywhere else in the interface.
    const isUsername = trimmed.startsWith('@');
    try {
      await enqueueJoinConversation({
        token: isUsername ? null : trimmed,
        publicUsername: isUsername ? trimmed.slice(1) : null,
        originDeviceId: account.deviceId,
      });
      worker?.notifyOutboxChanged();
      setSent(true);
    } catch {
      setError(t('status.localFailure'));
    }
  };

  return (
    <div className={dialogStyles.backdrop}>
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="join-title"
      >
        <h2 id="join-title" className={dialogStyles.title}>
          {t('invite.joinTitle')}
        </h2>
        <p className={dialogStyles.description}>{t('invite.joinDescription')}</p>

        <div className={styles.field}>
          <label htmlFor="join-token">{t('invite.joinField')}</label>
          <input
            id="join-token"
            className={styles.input}
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
            }}
          />
        </div>

        {sent ? (
          <p className={styles.hint} role="status">
            {t('invite.joinPending')}
          </p>
        ) : null}
        {error === null ? null : (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <div className={dialogStyles.actions}>
          <button type="button" className={dialogStyles.cancel} onClick={props.onClose}>
            {t('group.cancel')}
          </button>
          <button
            type="button"
            className={dialogStyles.confirm}
            disabled={value.trim().length === 0}
            onClick={() => {
              void join();
            }}
          >
            {t('invite.joinAction')}
          </button>
        </div>
      </div>
    </div>
  );
}
